import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faFilePdf, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import html2canvas from "html2canvas";

import mainbannerimage from "../../assets/Mainbanner/main.png";
import logo3 from "../../assets/logo/3.png";
import phoneicon from "../../assets/icons/phone.png";
import mailicon from "../../assets/icons/mail.png";

export default function CVTemplate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cvId } = useParams();
  const cvContentRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [savedData, setSavedData] = useState(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  const allLanguagesObj = {
    en: "English",
    ru: "Russian",
    hy: "Armenian",
  };

  const levelsObj = {
    beginner: "Beginner",
    intermediate: "Intermediate",
    advanced: "Advanced",
  };

  const levels2Obj = {
    beginner: "Beginner",
    intermediate: "Intermediate",
    advanced: "Advanced",
  };

  useEffect(() => {
    console.log("useEffect triggered");
    const fetchCV = async () => {
      try {
        const resp = await fetch(`/api/get_cv.php?id=${cvId}`);
        console.log("Fetch response:", resp);

        if (!resp.ok) {
          throw new Error(`HTTP error! status: ${resp.status}`);
        }

        const data = await resp.json();
        console.log("Fetched data:", data);

        if (data.success) {
          setSavedData(data.cv);
          // document.write(JSON.stringify(data))
        } else {
          alert("Ошибка: " + data.error);
        }
      } catch (err) {
        console.error("Ошибка загрузки:", err);
        setPdfError("Failed to load CV data");
      } finally {
        setLoading(false);
      }
    };

    if (cvId) {
      fetchCV();
    } else {
      setLoading(false);
      setPdfError("No CV ID provided");
    }
  }, [cvId]);

  // Wait for all images to load before allowing PDF generation
  useEffect(() => {
    if (!cvContentRef.current || !savedData) return;

    const images = cvContentRef.current.querySelectorAll("img");
    if (images.length === 0) {
      setImagesLoaded(true);
      return;
    }

    let loadedCount = 0;
    const totalImages = images.length;

    const checkAllImagesLoaded = () => {
      loadedCount++;
      if (loadedCount === totalImages) {
        setImagesLoaded(true);
      }
    };

    images.forEach((img) => {
      if (img.complete) {
        checkAllImagesLoaded();
      } else {
        img.addEventListener("load", checkAllImagesLoaded);
        img.addEventListener("error", checkAllImagesLoaded); // Also handle errors
      }
    });

    return () => {
      images.forEach((img) => {
        img.removeEventListener("load", checkAllImagesLoaded);
        img.removeEventListener("error", checkAllImagesLoaded);
      });
    };
  }, [savedData]);

  // ... (остальной код)

  const generatePDF = async () => {
    setGeneratingPDF(true);
    setPdfError(null);
    try {
      if (!savedData) {
        setPdfError("No CV data to send.");
        return;
      }

      // добавляем переводы заголовков
      const sectionTitles = {
        awards: t("Awards"),
        volunteering: t("Volunteer"),
        skills: t("skill"),
        computerSkills: t("computerskills"),
      };

      const formData = new FormData();
      formData.append(
        "cv_data",
        JSON.stringify({
          ...savedData,
          sectionTitles,
        })
      );

      const res = await fetch("/api/generate_pdf.php", {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(
          `Failed to generate PDF: ${res.statusText} - ${errorText}`
        );
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "cv.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Ошибка генерации PDF:", err);
      setPdfError(`Ошибка генерации PDF: ${err.message}`);
    } finally {
      setGeneratingPDF(false);
    }
  };

 const handleEdit = () => {
  // Передаём ID и все данные CV
  navigate(`/create_cv/${cvId}`, { 
    state: { 
      formData: savedData,
      cvId: cvId,
      isEditing: true
    } 
  });
};

  if (loading) return <div className="p-4 text-center">Загрузка...</div>;
  if (!savedData)
    return <div className="p-4 text-center">Нет данных для отображения CV</div>;

  return (
    <div className="max-w-4xl shadow-2xl opacity-0 animate-[showtitle_1s_forwards] relative py-5 mx-auto bg-gray-100 flex flex-col gap-5">
      {/* Кнопки */}
      <div className="flex gap-3 mb-5 px-5">
        <button
          onClick={handleEdit}
          className="flex items-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-black px-3 py-2 rounded-lg shadow-md transition-colors"
        >
          <FontAwesomeIcon icon={faPen} /> {t("edit")}
        </button>
        <button
          disabled={!savedData || generatingPDF || !imagesLoaded}
          onClick={generatePDF}
          className="flex items-center gap-2 bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white px-3 py-2 rounded-lg shadow-md transition-colors"
        >
          {generatingPDF ? (
            <>
              <FontAwesomeIcon icon={faSpinner} className="animate-spin" />{" "}
              {t("Generating")}...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faFilePdf} /> PDF
            </>
          )}
        </button>
      </div>

      {/* Error message */}
      {pdfError && (
        <div className="mx-5 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          <span className="block sm:inline">{pdfError}</span>
          <button
            onClick={() => setPdfError(null)}
            className="absolute top-0 right-0 p-3"
          >
            <svg
              className="h-5 w-5 text-red-500"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      )}

      {/* CV Контент с ref */}
      <div
        id="cv-content"
        ref={cvContentRef}
        className="w-full mx-auto bg-white flex flex-col p-8 shadow-md"
      >
        <div className="flex justify-start items-center">
          {savedData.photo && (
            <img
              loading="lazy"
              src={`/api/${savedData.photo}`}
              alt="Profile"
              className="min-w-40 h-40 border-[5px] border-white shadow-2xl rounded-lg object-contain"
            />
          )}
          {(savedData.name || savedData.lastname) && (
            <div
              className="w-full flex items-center relative p-6 text-white font-bold text-2xl tracking-wide"
              style={{
                backgroundImage: `url(${mainbannerimage})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <h1 className="text-4xl uppercase">
                {savedData.name || ""} <br /> {savedData.lastname || ""}
              </h1>
              <img
                className="max-w-[150px] absolute right-6 z-10 max-h-[150px] object-cover"
                src={logo3}
                alt=""
              />
            </div>
          )}
          {/* {savedData.about && (
            <div className="w-full px-5">
              <h2 className="text-[#006674] uppercase text-3xl font-bold border-b-2 border-[#006674] pb-1 mb-2">
                {t("aboutme")}
              </h2>
              <p className="text-md font-semibold text-gray-700">
                {savedData.about}
              </p>
            </div>
          )} */}
        </div>

        {/* Имя и баннер */}

        {/* Основной контент */}
        <div className="flex gap-6">
          {/* Левая колонка */}
          <div className="w-3/5 bg-gray-200 p-5 flex flex-col gap-4">
            {(savedData.phone || savedData.email) && (
              <div className="text-sm text-gray-700 flex flex-col gap-2 font-bold">
                {savedData.phone && (
                  <p className="flex items-center gap-2">
                    <img
                      className="w-[25px] h-[25px] object-contain"
                      src={phoneicon}
                      alt=""
                    />
                    {savedData.phone}
                  </p>
                )}
                {savedData.email && (
                  <p className="flex items-center gap-2">
                    <img
                      className="w-[25px] h-[25px] object-contain"
                      src={mailicon}
                      alt=""
                    />
                    {savedData.email}
                  </p>
                )}
              </div>
            )}

            {/* Languages */}
            {savedData.languages?.length > 0 && (
              <div>
                <h2 className="text-[#006674] uppercase font-bold border-b-2 border-[#006674] pb-1 mb-2">
                  {t("languages")}
                </h2>
                <table className="table-auto border-collapse w-full text-sm text-gray-700">
                  <thead>
                    <tr className="bg-[#006674] text-white">
                      <th className="text-sm">{t("language")}</th>
                      <th className="text-sm">{t("languageselect1")}</th>
                      <th className="text-sm">{t("languageselect2")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {savedData.languages
                      .filter(
                        (lang) =>
                          lang && (lang.name || lang.level || lang.level2)
                      )
                      .map((lang, i) => (
                        <tr key={i} className="border-b border-gray-300">
                          <td>
                            {allLanguagesObj?.[lang.name] || lang.name || ""}
                          </td>
                          <td>{levelsObj?.[lang.level] || lang.level || ""}</td>
                          <td>
                            {levels2Obj?.[lang.level2] || lang.level2 || ""}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Awards, Volunteering, Skills, Computer Skills */}
            {["awards", "volunteering", "skills", "computerSkills"].map(
              (key) => {
                const items = savedData[key];
                if (!items || items.length === 0) return null;
                const title =
                  key === "awards"
                    ? t("Awards")
                    : key === "volunteering"
                    ? t("Volunteer")
                    : key === "skills"
                    ? t("skill")
                    : t("computerskills");
                return (
                  <div key={key}>
                    <h2 className="text-[#006674] uppercase font-bold border-b-2 border-[#006674] pb-1 mb-2">
                      {title}
                    </h2>
                    <ul className="text-sm text-gray-700 list-disc list-inside">
                      {items.map((item, i) => (
                        <li key={i} className="font-bold">
                          {key === "awards"
                            ? `${item.title || "-"} (${item.field || "-"}, ${
                                item.qualification || "-"
                              }, ${item.year || "-"})`
                            : key === "volunteering"
                            ? `${item.role || "-"} – ${item.field || "-"}`
                            : key === "skills" || key === "computerSkills"
                            ? typeof item === "string"
                              ? item
                              : item.skill || "-"
                            : "-"}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              }
            )}
          </div>

          {/* Правая колонка */}
          <div className="w-4/5 p-5 flex flex-col gap-3">
            {savedData.location && (
              <div>
                <h2 className="text-[#006674] uppercase font-bold border-b-2 border-[#006674] pb-1 mb-2">
                  {t("location2")}
                </h2>
                <p className="text-sm font-semibold text-gray-700">
                  {savedData.location}
                </p>
              </div>
            )}

            {/* Education */}
            {savedData.education?.length > 0 && (
              <div>
                <h2 className="text-[#006674] text-md uppercase font-bold border-b-2 border-[#006674] pb-1 mb-1">
                  {t("Education")}
                </h2>
                {savedData.education.filter(Boolean).map((edu, i) => (
                  <div key={i} className="flex flex-col gap-1">
                    <p className="font-semibold text-[16px]">
                      {edu.degree || ""}
                    </p>
                    <p className="font-semibold text-[#006674] text-md">
                      {edu.school || ""}
                    </p>
                    {edu.Specialization && (
                      <p className="text-sm font-semibold">
                        {edu.Specialization}
                      </p>
                    )}
                    {(edu.start || edu.end) && (
                      <p className="text-xs font-bold text-black">
                        <span>{t("start")}</span> {edu.start || ""} –{" "}
                        <span>{t("end")}</span> {edu.end || "Present"}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Experience */}
            {savedData.experiences?.length > 0 && (
              <div>
                <h2 className="text-[#006674] uppercase font-bold border-b-2 border-[#006674] pb-1 mb-1">
                  {t("workexperiance")}
                </h2>
                {savedData.experiences.map((exp, i) => (
                  <div key={i} className="mb-2">
                    <p className="text-md font-bold text-[#006674]">
                      {exp.company || exp.name}
                    </p>
                    <p className="font-semibold text-sm">{exp.position}</p>
                    {(exp.start || exp.end) && (
                      <p className="text-xs font-bold text-black">
                        {exp.start || ""} – {exp.end || t("Present")}
                      </p>
                    )}
                    {exp.description && (
                      <p className="text-sm font-semibold text-gray-700">
                        {exp.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Additional Info */}
            {savedData.additionalInfo && (
              <div>
                <h2 className="text-[#006674] font-bold border-b-2 border-[#006674] pb-1 mb-1">
                  {t("additionalInfo")}
                </h2>
                <p className="text-sm font-semibold text-gray-700">
                  {savedData.additionalInfo}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
